// ---------------------------------------------------------------------------
// timeStretch.ts — streaming WSOLA time-stretch for the AI voice.
//
// Gemini Live has no speaking-rate setting, and its voice does not obey pace
// instructions in any measurable way (smoke 2026-09-26, same script: natural
// 15.5, brisk 15.5, fast 16.1 transcript characters per second of audio).
// Kozhikode callers talk fast, so the played audio is time-compressed HERE,
// pitch preserved: waveform-similarity overlap-add — 25 ms Hann frames at
// 50 % overlap; every synthesis step advances the input by hop × rate and,
// within ±10 ms, picks the frame that best continues the previous one
// (normalised cross-correlation) before overlap-adding it. At rate 1 samples
// pass through untouched. Pure functions, no DOM — the unit test runs in Node.
// ---------------------------------------------------------------------------

export interface TimeStretcher {
  /** Playback rate: 1 = unchanged, 1.15 = 15 % faster; clamped to [1, 2]. */
  setRate(rate: number): void;
  rate(): number;
  /** Feed input samples; returns the output ready so far (possibly empty). */
  push(samples: Float32Array): Float32Array;
  /** End of an utterance: return whatever is held back (unstretched) and reset. */
  flush(): Float32Array;
  /** Drop everything held back (barge-in). */
  reset(): void;
}

const FRAME_MS = 25;
const TOLERANCE_MS = 10;
const SEARCH_STEP = 2;
export const MAX_STRETCH_RATE = 2;

export function createTimeStretcher(sampleRate = 24_000): TimeStretcher {
  const N = 2 * Math.round((sampleRate * FRAME_MS) / 2000); // even frame length: 600 @ 24 kHz
  const H = N / 2; // synthesis hop
  const TOL = Math.round((sampleRate * TOLERANCE_MS) / 1000); // ±240 samples
  // Periodic Hann: frames at hop N/2 sum to exactly 1 (constant overlap-add).
  const window = new Float32Array(N);
  for (let n = 0; n < N; n++) window[n] = 0.5 - 0.5 * Math.cos((2 * Math.PI * n) / N);

  let rate = 1;
  let buf = new Float32Array(0); // input samples, buf[0] is absolute index `base`
  let base = 0;
  let inPos = 0; // absolute nominal analysis position of the next frame (fractional)
  let prevPos = -1; // absolute start of the previously chosen frame; -1 = none yet
  let tail = new Float32Array(H); // windowed second half of the previous frame, pending overlap-add
  let started = false;

  const available = () => base + buf.length;

  function append(samples: Float32Array) {
    const next = new Float32Array(buf.length + samples.length);
    next.set(buf, 0);
    next.set(samples, buf.length);
    buf = next;
  }

  function resetState() {
    buf = new Float32Array(0);
    base = 0;
    inPos = 0;
    prevPos = -1;
    tail = new Float32Array(H);
    started = false;
  }

  /** Offset (within ±TOL of `nominal`) at which the input continues the previous frame most smoothly. */
  function bestOffset(nominal: number): number {
    if (prevPos < 0) return 0;
    const tStart = prevPos + H - base; // the natural continuation of the previous frame
    const dMin = Math.max(-TOL, base - nominal);
    const dMax = Math.min(TOL, available() - N - nominal);
    let best = 0;
    let bestScore = -Infinity;
    for (let d = dMin; d <= dMax; d += SEARCH_STEP) {
      const cStart = nominal + d - base;
      let dot = 0;
      let energy = 1e-9;
      for (let i = 0; i < H; i++) {
        const c = buf[cStart + i]!;
        dot += buf[tStart + i]! * c;
        energy += c * c;
      }
      const score = dot / Math.sqrt(energy);
      if (score > bestScore) {
        bestScore = score;
        best = d;
      }
    }
    return best;
  }

  function stretch(): Float32Array {
    const outs: Float32Array[] = [];
    while (inPos + TOL + N <= available()) {
      const nominal = Math.round(inPos);
      const start = nominal + bestOffset(nominal) - base;
      const out = new Float32Array(H);
      if (!started) {
        // First frame of an utterance: raw first half (no fade-in), then the windowed tail.
        for (let i = 0; i < H; i++) out[i] = buf[start + i]!;
        started = true;
      } else {
        for (let i = 0; i < H; i++) out[i] = tail[i]! + buf[start + i]! * window[i]!;
      }
      for (let i = 0; i < H; i++) tail[i] = buf[start + H + i]! * window[H + i]!;
      prevPos = start + base;
      inPos += H * rate;
      outs.push(out);
    }
    // Keep only what the next step can still look at (the template and the search span).
    const keepFrom = Math.min(prevPos < 0 ? inPos : prevPos, inPos - TOL);
    const drop = Math.floor(keepFrom) - base;
    if (drop > 0) {
      buf = buf.slice(drop);
      base += drop;
    }
    return concat(outs);
  }

  /** Everything after the last emitted sample, unstretched: the previous frame's raw second half and what follows it. */
  function heldBack(): Float32Array {
    const from = prevPos < 0 ? Math.round(inPos) : prevPos + H;
    return buf.slice(Math.max(0, from - base));
  }

  return {
    setRate(r) {
      rate = Math.min(MAX_STRETCH_RATE, Math.max(1, Number.isFinite(r) ? r : 1));
    },
    rate: () => rate,
    push(samples) {
      if (rate === 1) {
        if (buf.length === 0 && prevPos < 0) return samples; // pass-through
        const rest = heldBack(); // release what an earlier rate held back, then pass through
        resetState();
        return concat([rest, samples]);
      }
      append(samples);
      return stretch();
    },
    flush() {
      const rest = heldBack();
      resetState();
      return rest;
    },
    reset: resetState,
  };
}

function concat(parts: Float32Array[]): Float32Array {
  if (parts.length === 1) return parts[0]!;
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Float32Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}
